import { Loading } from '@/components/UI/Loading'
import styles from '@/styles/Scanner.module.css'
import type { BrowserCodeReader, IScannerControls } from '@zxing/browser'
import { BrowserMultiFormatReader, BrowserQRCodeReader } from '@zxing/browser'
import * as Zxing from '@zxing/library'
import type { Exception } from '@zxing/library'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

const SCAN_SUCCESS_DELAY = 3500

export type ScanMode = 'qr' | 'barcode'

export interface ScannerProps {
  onRead?: (success: Zxing.Result) => Promise<boolean | undefined>
  ready?: boolean
  guide?: boolean
  /** 読み取りモード: 'qr' = QRコード専用 / 'barcode' = バーコード含む全フォーマット */
  mode?: ScanMode
  isPaused?: boolean
  /** クーポンを読み込む場合かどうか */
  coupon?: boolean
  /** 下部の説明を押下した時の遷移先 */
  href?: string
  /** カメラ許可が得られなかった（キャンセル・拒否）時のコールバック */
  onCameraPermissionDenied?: () => void
}

/**
 * このコンポーネントのstorybookは作成しない。
 * ScanQrOverlayでのstorybookで確認するため。
 * */
const Scanner = React.memo(
  ({
    onRead,
    ready = false,
    guide,
    mode = 'barcode',
    isPaused = false,
    coupon = false,
    href,
    onCameraPermissionDenied,
  }: ScannerProps) => {
    const { t } = useTranslation()
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const readerRef = useRef<BrowserCodeReader | null>(null)
    const controlRef = useRef<IScannerControls | null>(null)
    const [isLoading, setLoading] = useState(true)
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
    // camera-permission-denied リスナーの参照（canplay 成功時に削除するため ref 保持）
    const permissionDeniedListenerRef = useRef<(() => void) | null>(null)
    // 最新のコールバック props を ref で保持し、identity 変化でカメラ起動 effect が再実行されないようにする
    const onReadRef = useRef(onRead)
    onReadRef.current = onRead
    const onCameraPermissionDeniedRef = useRef(onCameraPermissionDenied)
    onCameraPermissionDeniedRef.current = onCameraPermissionDenied
    // stopCamera のたびにインクリメントし、await 中（in-flight）の startCamera を無効化する世代カウンタ
    const generationRef = useRef(0)

    const onScanFailed = useCallback((error: Exception) => {
      // エラー
      if (error.message) console.log('バーコードエラー', error.message)
    }, [])
    const onScanSuccess = useCallback(async (success: Zxing.Result) => {
      if (!success) return
      await onReadRef.current?.(success)
    }, [])

    const stopCamera = useCallback(() => {
      generationRef.current++
      // カメラストリームとデコードプロセスの停止
      if (controlRef.current) {
        controlRef.current.stop()
        controlRef.current = null
      }

      if (readerRef.current) {
        readerRef.current = null
      }

      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream
        for (const track of stream.getTracks()) {
          track.stop()
        }
      }
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.srcObject = null
      }
    }, [])

    const startCamera = useCallback(async () => {
      // 既存ストリームを必ず止めてから起動し、アクティブなストリームを常に高々1本に保つ
      stopCamera()
      const generation = generationRef.current
      // 前回の camera-permission-denied リスナーが残っていれば削除してから再登録
      if (permissionDeniedListenerRef.current) {
        videoRef.current?.removeEventListener('camera-permission-denied', permissionDeniedListenerRef.current)
      }
      const handlePermissionDenied = () => {
        setLoading(false)
      }
      permissionDeniedListenerRef.current = handlePermissionDenied

      // canplay は { once: true } で登録し、成功時に camera-permission-denied リスナーも削除する
      videoRef.current?.addEventListener(
        'canplay',
        () => {
          setLoading(false)
          // 成功時: camera-permission-denied リスナーを明示的に解除
          if (permissionDeniedListenerRef.current) {
            videoRef.current?.removeEventListener('camera-permission-denied', permissionDeniedListenerRef.current)
            permissionDeniedListenerRef.current = null
          }
        },
        { once: true },
      )
      // 失敗時にローディングを解除するためのカスタムイベントリスナー
      videoRef.current?.addEventListener('camera-permission-denied', handlePermissionDenied, { once: true })

      const hints = new Map<Zxing.DecodeHintType, Zxing.BarcodeFormat[]>()
      const formats = Object.values(Zxing.BarcodeFormat).filter(
        (format): format is Zxing.BarcodeFormat => typeof format === 'number',
      )
      hints.set(Zxing.DecodeHintType.POSSIBLE_FORMATS, formats)
      const options = {
        delayBetweenScanSuccess: SCAN_SUCCESS_DELAY,
      }

      const readerType =
        mode === 'qr' ? new BrowserQRCodeReader(undefined, options) : new BrowserMultiFormatReader(hints, options)

      readerRef.current = readerType
      if (!videoRef.current) {
        return
      }
      try {
        const control = await readerRef.current.decodeFromConstraints(
          {
            video: { facingMode: 'environment' },
          },
          videoRef.current,
          (success, error) => {
            if (error) onScanFailed(error)
            if (success) onScanSuccess(success)
          },
        )
        // await 中に stopCamera（unmount・pause・再起動）が呼ばれた場合はこの control を破棄する
        // （videoRef.current は unmount 時 null になる）
        if (generation !== generationRef.current || !videoRef.current) {
          control.stop()
          return
        }
        controlRef.current = control
      } catch (e) {
        // await 中に stopCamera（unmount・pause・再起動）が呼ばれていた場合、
        // この起動は破棄済みのため後始末（インターバル停止・ダイアログ表示）を行わない
        if (generation !== generationRef.current) return
        // 例外種別に関わらずインターバル停止とローディング解除を行う
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
        }
        videoRef.current?.dispatchEvent(new Event('camera-permission-denied'))
        if (e instanceof DOMException && e.name === 'NotAllowedError') {
          // カメラ許可のキャンセル・拒否: ダイアログ表示コールバックを呼ぶ
          onCameraPermissionDeniedRef.current?.()
        } else {
          console.error('カメラ起動エラー', e)
        }
      }
    }, [mode, stopCamera, onScanFailed, onScanSuccess])

    const restartCamera = useCallback(() => {
      if (videoRef.current && controlRef.current) {
        const video = videoRef.current
        const videoPaused = video.paused || video.readyState !== 4
        const videoFrozen = video.currentTime === 0 || (video.currentTime === video.duration && video.duration !== 0)

        if (videoPaused || videoFrozen) {
          // 既存 control の停止は startCamera 冒頭の stopCamera に委譲する
          startCamera()
        }
      }
    }, [startCamera])

    useEffect(() => {
      if (isPaused) {
        stopCamera()
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
        }
        return
      }

      startCamera()

      if (!intervalIdRef.current) {
        const checkInterval = setInterval(restartCamera, 1000)
        intervalIdRef.current = checkInterval
      }

      return () => {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current)
          intervalIdRef.current = null
        }
        // isPaused の値によらず必ず停止する（unmount 時のカメラ残留を防ぐ）
        stopCamera()
      }
    }, [startCamera, stopCamera, restartCamera, isPaused])

    useLayoutEffect(() => {
      const main = document.querySelector('main')
      if (!main) return

      const prevBackground = main.style.background
      const prevHeight = main.style.height
      const prevPadding = main.style.padding

      main.style.background = '#000'
      main.style.height = 'calc(100vh - 75px)'
      main.style.padding = '0px'

      return () => {
        main.style.background = prevBackground
        main.style.height = prevHeight
        main.style.padding = prevPadding
      }
    }, [])

    const message =
      mode === 'qr'
        ? t('spot_detail.qr.please_scan_the_qr_code', 'QRを読み込んでください')
        : t('spot_detail.qr.please_scan_the_barcode', 'バーコードを読み込んでください')

    return (
      <section className="relative flex h-full items-center justify-center">
        {isLoading && ready && <Loading />}
        <div className={styles.overlay}>
          {coupon && href ? (
            <span className={styles.overlay_text}>
              <a href={href}>
                {ready
                  ? t('reward_detail.if_you_cannot_read_qr', 'QRコードを読み込めない方はこちら')
                  : t('spot_detail.qr.wait_a_moment', '少々お待ちください')}
              </a>
            </span>
          ) : (
            <span className={styles.overlay_text}>
              {ready ? message : t('spot_detail.qr.wait_a_moment', '少々お待ちください')}
            </span>
          )}
        </div>
        {guide && <div className={styles.guide} />}
        <video className={styles.video} ref={videoRef} playsInline muted />
      </section>
    )
  },
)

Scanner.displayName = 'Scanner'

export default Scanner
